// src/services/chatService.js
// Firebase Realtime Database chat service

import {
  ref,
  push,
  onValue,
  off,
  set,
  update,
  serverTimestamp,
  query,
  orderByChild,
  get,
  increment,
} from "firebase/database";
import { rtdb } from "./firebase";

/**
 * Create or update a chat room for an order.
 */
export async function createChatRoom(order) {
  const chatRef = ref(rtdb, `chats/${order.id}/info`);
  const snap = await get(chatRef);
  if (snap.exists()) return; // already exists

  await set(chatRef, {
    orderId: order.id,
    userId: order.userId,
    userEmail: order.userEmail,
    gameTitle: order.gameTitle,
    status: "open",
    unreadAdmin: 0,
    unreadUser: 0,
    lastMessage: "",
    createdAt: serverTimestamp(),
  });
}

/**
 * Send a message to a chat room.
 */
export async function sendMessage(orderId, text, user, role = "user") {
  const msgsRef = ref(rtdb, `chats/${orderId}/messages`);
  const infoRef = ref(rtdb, `chats/${orderId}/info`);

  // Push message
  await push(msgsRef, {
    text,
    senderId: user.uid,
    senderName: user.displayName || user.email,
    role,
    timestamp: serverTimestamp(),
    read: false,
  });

  // Update chat info — increment unread counter for the other side
  await update(infoRef, {
    lastMessage: text.slice(0, 80),
    updatedAt: serverTimestamp(),
    [role === "admin" ? "unreadUser" : "unreadAdmin"]: increment(1),
  });
}

/**
 * Subscribe to messages in a chat room (real-time).
 * Returns unsubscribe function.
 */
export function subscribeToMessages(orderId, callback) {
  const msgsRef = query(
    ref(rtdb, `chats/${orderId}/messages`),
    orderByChild("timestamp")
  );

  const handler = (snap) => {
    const msgs = [];
    snap.forEach((child) => {
      msgs.push({ id: child.key, ...child.val() });
    });
    callback(msgs);
  };

  onValue(msgsRef, handler);
  return () => off(msgsRef, "value", handler);
}

/**
 * Subscribe to all chat rooms (admin use).
 */
export function subscribeToAllChats(callback) {
  const chatsRef = ref(rtdb, "chats");

  const handler = (snap) => {
    const chats = [];
    snap.forEach((child) => {
      const info = child.val()?.info;
      if (info) chats.push({ id: child.key, ...info });
    });
    // Sort by updatedAt descending
    chats.sort((a, b) => (b.updatedAt || 0) - (a.updatedAt || 0));
    callback(chats);
  };

  onValue(chatsRef, handler);
  return () => off(chatsRef, "value", handler);
}

/**
 * Mark messages as read for a role.
 */
export async function markAsRead(orderId, role) {
  const infoRef = ref(rtdb, `chats/${orderId}/info`);
  await update(infoRef, {
    [role === "admin" ? "unreadAdmin" : "unreadUser"]: 0,
  });
}
